import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useLogoutMutation } from 'api';
import { GoBackButtonWithTitle, NewConfirmDialog, PageHeaderBoldTitle } from 'components';
import { useAuth, useModal } from 'hooks';
import { Links } from 'settings';

import { SubscriptionsList, PeriodSwitcher, UnauthorizedToast } from '../../components';
import { ButtonW, ContentW, HeaderW, PageW } from './styled';
import { modalSx, modalTitleSx } from './styles';
import { useSubscriptionsPage } from './useSubscriptionsPage';

export function SubscriptionsPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    goBack,
    isExpired,
    open,
    period,
    setPeriod,
    handleCloseModal,
    isToastOpen,
    handleToastClose,
  } = useSubscriptionsPage();

  const { removeCurrentUser } = useAuth();
  const { logoutMutation } = useLogoutMutation();

  const {
    handleCloseModal: closeConfirmLogoutModal,
    isOpen: isOpenConfirmLogoutModal,
    handleOpenModal: openConfirmLogoutModal,
  } = useModal();

  async function handleConfirmLogout() {
    await logoutMutation();
    removeCurrentUser();
    closeConfirmLogoutModal();
    navigate(Links.auth.signIn);
    if (Notification.permission === 'granted') {
      window.location.reload();
    }
  }

  return (
    <PageW>
      <NewConfirmDialog
        title={t('subscriptions.modals.onPaymentCompleted')}
        onConfirm={handleCloseModal}
        open={open}
        titleSx={modalTitleSx}
        withLogo
        dialogSx={modalSx}
        submitLabel={t('subscriptions.modals.startSubscription.submit')}
      />
      <UnauthorizedToast open={isToastOpen} onClose={handleToastClose} />

      <HeaderW>
        <GoBackButtonWithTitle
          title={t('common.back')}
          onClick={() => navigate(-1)}
          containerSx={{
            marginTop: 0,
            maxWidth: '100%',
            marginBottom: '16px',
          }}
          Actions={
            isExpired ? (
              <ButtonW variant="text" color="info" onClick={openConfirmLogoutModal}>
                {t('subscriptions.logout.text')}
              </ButtonW>
            ) : (
              <></>
            )
          }
        />
        <PageHeaderBoldTitle
          title={t('subscriptions.pageTitle')}
          onClickCloseIcon={goBack}
          withCloseBtn={!isExpired}
          buttonPosition="static"
        />
      </HeaderW>
      <ContentW>
        <PeriodSwitcher period={period} setPeriod={setPeriod} />
        <SubscriptionsList period={period} />
      </ContentW>
      <NewConfirmDialog
        title={t('subscriptions.modals.onPaymentCompleted')}
        onConfirm={handleCloseModal}
        open={open}
        titleSx={modalTitleSx}
        withLogo
        dialogSx={modalSx}
        submitLabel={t('subscriptions.modals.startSubscription.submit')}
      />
      <NewConfirmDialog
        title={t('profile.logout.confirmText')}
        subTitle={t('profile.logout.subText')}
        open={isOpenConfirmLogoutModal}
        onConfirm={handleConfirmLogout}
        onClose={closeConfirmLogoutModal}
        submitLabel={t('profile.logout.confirmYes')}
        cancelLabel={t('profile.logout.confirmNo')}
      />
    </PageW>
  );
}
